import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut
} from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  getDoc,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch
} from "firebase/firestore";
import { auth, db } from "./firebase.js";
import { calculatePredictionPoints, outcomeDelta } from "../utils/scoring.js";
import { normalizeParticipantType } from "../utils/csv.js";
import { findUserRank, rankUsers } from "../utils/ranking.js";
import { normalizeRegistrationPayload } from "../utils/registration.js";

function normalizeTimestamp(value) {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (value.toDate) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  return value;
}

function normalizeDoc(snapshot) {
  if (!snapshot.exists()) return null;
  const data = snapshot.data();
  return {
    id: snapshot.id,
    ...data,
    dateTime: normalizeTimestamp(data.dateTime),
    lockAt: normalizeTimestamp(data.lockAt),
    createdAt: normalizeTimestamp(data.createdAt),
    updatedAt: normalizeTimestamp(data.updatedAt),
    importedAt: normalizeTimestamp(data.importedAt),
    activatedAt: normalizeTimestamp(data.activatedAt)
  };
}

function normalizeResultScore(value) {
  const score = Number(value);
  if (!Number.isInteger(score) || score < 0 || score > 50) {
    throw new Error("Ingresa marcadores enteros entre 0 y 50.");
  }
  return score;
}

function isKnockoutStage(stage) {
  return stage && stage !== "group";
}

function normalizePenaltyScore(value) {
  const score = Number(value);
  if (!Number.isInteger(score) || score < 0 || score > 30) {
    throw new Error("Ingresa penales enteros entre 0 y 30.");
  }
  return score;
}

function buildResultMetadata(match, result, winnerTeam, tiebreaker) {
  if (!isKnockoutStage(match.stage)) {
    return { winnerTeam: "", tiebreaker: null };
  }

  if (result.home > result.away) {
    return { winnerTeam: match.homeTeam, tiebreaker: null };
  }

  if (result.away > result.home) {
    return { winnerTeam: match.awayTeam, tiebreaker: null };
  }

  if (![match.homeTeam, match.awayTeam].includes(winnerTeam)) {
    throw new Error("Selecciona quién avanzó por penales.");
  }

  const penaltyHome = normalizePenaltyScore(tiebreaker?.home);
  const penaltyAway = normalizePenaltyScore(tiebreaker?.away);
  if (penaltyHome === penaltyAway) {
    throw new Error("El marcador de penales no puede quedar empatado.");
  }
  if (winnerTeam === match.homeTeam && penaltyHome < penaltyAway) {
    throw new Error("El ganador por penales no coincide con el marcador de penales.");
  }
  if (winnerTeam === match.awayTeam && penaltyAway < penaltyHome) {
    throw new Error("El ganador por penales no coincide con el marcador de penales.");
  }

  return {
    winnerTeam,
    tiebreaker: {
      type: "penalties",
      home: penaltyHome,
      away: penaltyAway
    }
  };
}

function sameTiebreaker(previous, next) {
  if (!previous && !next) return true;
  return (
    previous?.type === next?.type &&
    previous?.home === next?.home &&
    previous?.away === next?.away
  );
}

const RESULT_BATCH_PREDICTION_LIMIT = 50;
const RESULT_BATCH_CONCURRENCY = 1;
const FIRESTORE_STEP_TIMEOUT_MS = 25000;

function isResourceExhausted(error) {
  return error?.code === "resource-exhausted" || /quota|resource[_ -]?exhausted|too many requests/i.test(error?.message || "");
}

async function runFirestoreStep(promise, timeoutMessage) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(timeoutMessage)), FIRESTORE_STEP_TIMEOUT_MS);
  });

  try {
    return await Promise.race([promise, timeout]);
  } catch (error) {
    if (isResourceExhausted(error)) {
      throw new Error("Firestore llegó a su límite de cuota. Cierra las pestañas abiertas e intenta más tarde o habilita facturación para ampliar la cuota.");
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

function chunkArray(items, size) {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
}

export const firebaseStore = {
  mode: "firebase",
  onAuthStateChanged(callback) {
    return onAuthStateChanged(auth, callback);
  },
  async loginUser(email, password) {
    return signInWithEmailAndPassword(auth, email.toLowerCase(), password);
  },
  async sendPasswordReset(email) {
    return sendPasswordResetEmail(auth, email.toLowerCase());
  },
  async registerUser(payload) {
    const userInput = normalizeRegistrationPayload(payload);
    const credential = await createUserWithEmailAndPassword(auth, userInput.email, userInput.password);
    const profile = {
      uid: credential.user.uid,
      email: userInput.email,
      name: userInput.name,
      role: "user",
      participantType: userInput.participantType,
      professionalLicense: userInput.participantType === "medico" ? userInput.professionalLicense : "",
      employeeCode: userInput.participantType === "colaborador" ? userInput.employeeCode : "",
      totalPoints: 0,
      correctOutcomes: 0,
      exactScores: 0,
      predictionsCount: 0,
      createdAt: serverTimestamp()
    };

    try {
      await setDoc(doc(db, "users", credential.user.uid), profile);
    } catch (error) {
      await credential.user.delete().catch(() => {});
      throw error;
    }

    return credential;
  },
  logoutUser() {
    return signOut(auth);
  },
  subscribeWorkspace(uid, callback) {
    const state = {
      profile: null,
      currentUserRank: null,
      teams: [],
      matches: [],
      predictions: [],
      ranking: [],
      chatMessages: [],
      authorizedEmails: []
    };
    const unsubscribers = [];
    let authorizedUnsubscribe = null;

    const emit = () => callback({ ...state });

    const refreshCurrentUserRank = () => {
      state.currentUserRank = findUserRank(state.ranking, state.profile?.uid);
    };

    unsubscribers.push(
      onSnapshot(doc(db, "users", uid), (snapshot) => {
        state.profile = normalizeDoc(snapshot);
        if (state.profile?.role === "admin" && !authorizedUnsubscribe) {
          authorizedUnsubscribe = onSnapshot(query(collection(db, "authorizedEmails"), orderBy("email")), (querySnapshot) => {
            state.authorizedEmails = querySnapshot.docs.map(normalizeDoc);
            emit();
          });
          unsubscribers.push(() => authorizedUnsubscribe?.());
        }
        refreshCurrentUserRank();
        emit();
      })
    );

    unsubscribers.push(
      onSnapshot(query(collection(db, "teams"), orderBy("group"), orderBy("name")), (querySnapshot) => {
        state.teams = querySnapshot.docs.map(normalizeDoc);
        emit();
      })
    );

    unsubscribers.push(
      onSnapshot(query(collection(db, "matches"), orderBy("dateTime")), (querySnapshot) => {
        state.matches = querySnapshot.docs.map(normalizeDoc);
        emit();
      })
    );

    unsubscribers.push(
      onSnapshot(query(collection(db, "predictions"), where("uid", "==", uid)), (querySnapshot) => {
        state.predictions = querySnapshot.docs.map(normalizeDoc);
        emit();
      })
    );

    const rankingQuery = query(collection(db, "users"), orderBy("totalPoints", "desc"));

    unsubscribers.push(
      onSnapshot(rankingQuery, (querySnapshot) => {
        state.ranking = rankUsers(querySnapshot.docs.map(normalizeDoc));
        refreshCurrentUserRank();
        emit();
      })
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  },
  async sendChatMessage({ uid, text }) {
    const cleanText = text.trim();
    if (!cleanText) throw new Error("Escribe un mensaje antes de enviarlo.");
    if (cleanText.length > 280) throw new Error("El mensaje debe tener 280 caracteres o menos.");

    return addDoc(collection(db, "chatMessages"), {
      uid: auth.currentUser?.uid || uid,
      text: cleanText,
      createdAt: serverTimestamp()
    });
  },
  async savePrediction({ uid, matchId, homeScore, awayScore }) {
    const matchRef = doc(db, "matches", matchId);
    const matchSnap = await getDoc(matchRef);
    const match = normalizeDoc(matchSnap);

    if (!match) throw new Error("No encontramos ese partido.");
    if (new Date(match.lockAt).getTime() <= Date.now()) {
      throw new Error("Este partido ya está cerrado para predicciones.");
    }

    const predictionRef = doc(db, "predictions", `${uid}_${matchId}`);
    const previous = await getDoc(predictionRef);
    const nowPayload = {
      uid,
      matchId,
      homeScore: Number(homeScore),
      awayScore: Number(awayScore),
      updatedAt: serverTimestamp()
    };

    await setDoc(
      predictionRef,
      previous.exists()
        ? nowPayload
        : {
            ...nowPayload,
            pointsEarned: 0,
            outcomeType: "pending",
            createdAt: serverTimestamp()
          },
      { merge: true }
    );

    return previous.exists() ? "updated" : "created";
  },
  async importAuthorizedEmails(rows) {
    const batch = writeBatch(db);
    let imported = 0;
    let duplicates = 0;

    for (const row of rows) {
      const ref = doc(db, "authorizedEmails", row.email);
      const snapshot = await getDoc(ref);
      if (snapshot.exists()) {
        duplicates += 1;
        continue;
      }
      batch.set(ref, {
        name: row.name,
        email: row.email,
        participantType: normalizeParticipantType(row.participantType),
        professionalLicense: normalizeParticipantType(row.participantType) === "medico" ? row.professionalLicense || "" : "",
        employeeCode: normalizeParticipantType(row.participantType) === "colaborador" ? row.employeeCode || "" : "",
        used: false,
        importedAt: serverTimestamp()
      });
      imported += 1;
    }

    await batch.commit();
    return { imported, duplicates };
  },
  async importUsers() {
    throw new Error("Por seguridad, crea usuarios con passwords usando `npm run import-users -- archivo.csv` desde tu máquina.");
  },
  updateMatch(matchId, patch) {
    const payload = { ...patch };
    if (payload.dateTime) payload.dateTime = new Date(payload.dateTime);
    if (payload.lockAt) payload.lockAt = new Date(payload.lockAt);
    return updateDoc(doc(db, "matches", matchId), payload);
  },
  async saveMatchResult({ matchId, home, away, winnerTeam, tiebreaker }, onProgress = () => {}) {
    const matchRef = doc(db, "matches", matchId);
    onProgress({ message: "Leyendo partido..." });
    const matchSnapshot = await runFirestoreStep(
      getDoc(matchRef),
      "Firestore tardó demasiado leyendo el partido. Puede estar limitado por cuota."
    );
    const match = normalizeDoc(matchSnapshot);
    if (!match) throw new Error("No encontramos ese partido.");

    const result = { home: normalizeResultScore(home), away: normalizeResultScore(away) };
    const metadata = buildResultMetadata(match, result, winnerTeam, tiebreaker);
    const resultFields = {
      result,
      winnerTeam: metadata.winnerTeam,
      tiebreaker: metadata.tiebreaker
    };
    const sameScore =
      match.status === "finished" &&
      match.scoringStatus === "complete" &&
      match.result?.home === result.home &&
      match.result?.away === result.away;
    const sameMetadata =
      (match.winnerTeam || "") === metadata.winnerTeam &&
      sameTiebreaker(match.tiebreaker, metadata.tiebreaker);

    if (sameScore && sameMetadata) {
      onProgress({ message: "Ese resultado ya estaba confirmado." });
      return { predictionsCount: 0, skipped: true };
    }

    if (sameScore) {
      await runFirestoreStep(
        updateDoc(matchRef, {
          ...resultFields,
          status: "finished",
          scoringStatus: "complete",
          scoringError: "",
          scoringCompletedAt: serverTimestamp()
        }),
        "Firestore tardó demasiado actualizando el ganador del partido."
      );
      onProgress({ message: "Ganador por penales actualizado." });
      return { predictionsCount: 0, skipped: true };
    }

    const predictionQuery = query(collection(db, "predictions"), where("matchId", "==", matchId));
    onProgress({ message: "Leyendo pronósticos..." });
    const predictionSnapshots = (
      await runFirestoreStep(
        getDocs(predictionQuery),
        "Firestore tardó demasiado leyendo pronósticos. Puede estar limitado por cuota."
      )
    ).docs;
    const chunks = chunkArray(predictionSnapshots, RESULT_BATCH_PREDICTION_LIMIT);
    let completedPredictions = 0;

    onProgress({
      message: predictionSnapshots.length
        ? `Recalculando ${predictionSnapshots.length} pronósticos. Mantén esta ventana abierta...`
        : "Guardando resultado..."
    });

    await runFirestoreStep(
      updateDoc(matchRef, {
        status: "finished",
        ...resultFields,
        scoringStatus: "running",
        scoringError: "",
        scoringStartedAt: serverTimestamp(),
        scoredPredictions: 0
      }),
      "Firestore tardó demasiado iniciando el guardado del resultado. Puede estar limitado por cuota."
    );

    const commitChunk = async (snapshots) => {
      const batch = writeBatch(db);
      let writes = 0;

      snapshots.forEach((predictionSnapshot) => {
        const prediction = normalizeDoc(predictionSnapshot);
        const next = calculatePredictionPoints(prediction, result, match.multiplier);
        const previousPoints = prediction.pointsEarned || 0;
        const previousType = prediction.outcomeType || "none";
        const delta = outcomeDelta(previousType, next.outcomeType);
        const pointsDelta = Number((next.points - previousPoints).toFixed(2));
        const predictionChanged = previousPoints !== next.points || previousType !== next.outcomeType;
        const userChanged = pointsDelta !== 0 || delta.correctOutcomes !== 0 || delta.exactScores !== 0;

        if (predictionChanged) {
          batch.update(predictionSnapshot.ref, {
            pointsEarned: next.points,
            outcomeType: next.outcomeType,
            updatedAt: serverTimestamp()
          });
          writes += 1;
        }

        if (userChanged) {
          batch.update(doc(db, "users", prediction.uid), {
            totalPoints: increment(pointsDelta),
            correctOutcomes: increment(delta.correctOutcomes),
            exactScores: increment(delta.exactScores)
          });
          writes += 1;
        }
      });

      if (writes > 0) {
        await runFirestoreStep(
          batch.commit(),
          "Firestore tardó demasiado guardando puntos. Puede estar limitado por cuota."
        );
      }
      completedPredictions += snapshots.length;
      await runFirestoreStep(
        updateDoc(matchRef, {
          scoredPredictions: completedPredictions,
          scoringUpdatedAt: serverTimestamp()
        }),
        "Firestore tardó demasiado actualizando el avance. Puede estar limitado por cuota."
      );
      onProgress({
        message: `Recalculando puntos ${completedPredictions}/${predictionSnapshots.length}...`,
        completed: completedPredictions,
        total: predictionSnapshots.length
      });
    };

    try {
      for (let index = 0; index < chunks.length; index += RESULT_BATCH_CONCURRENCY) {
        await Promise.all(chunks.slice(index, index + RESULT_BATCH_CONCURRENCY).map(commitChunk));
      }

      await runFirestoreStep(
        updateDoc(matchRef, {
          ...resultFields,
          status: "finished",
          scoringStatus: "complete",
          scoringError: "",
          scoringCompletedAt: serverTimestamp(),
          scoredPredictions: predictionSnapshots.length
        }),
        "Firestore tardó demasiado finalizando el resultado. Puede estar limitado por cuota."
      );
      onProgress({ message: "Resultado confirmado y puntos recalculados." });
      return { predictionsCount: predictionSnapshots.length, skipped: false };
    } catch (error) {
      await updateDoc(matchRef, {
        scoringStatus: "failed",
        scoringError: error.message,
        scoringFailedAt: serverTimestamp()
      }).catch(() => {});
      throw error;
    }
  }
};
